import { Injectable, InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import {
    CompanyUserService,
    CompanyUserSummary,
    PublishState,
} from '@treasury/domain/company/user';
import {
    SideDrawerButton,
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html } from 'lit';
import { CompanyUserActionType } from '../';
import { RowActionCommand, RowActionCommandResult } from '../../base.cmd';
import {
    ApproveOrRejectConfirmation,
    ApproveOrRejectConfirmationData,
    Decision,
} from './approve-or-reject-drawer/approve-or-reject.component';

@Injectable()
export class ApproveOrRejectCompanyUserCommand implements RowActionCommand<
    CompanyUserSummary,
    CompanyUserActionType
> {
    constructor() {}

    @InjectProperty()
    declare private readonly sideDrawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    public command(
        decision: Decision,
        companyId?: number
    ): RowActionCommandResult<CompanyUserSummary, CompanyUserActionType> {
        return {
            type:
                decision === Decision.Approve
                    ? CompanyUserActionType.Approve
                    : CompanyUserActionType.Reject,
            title: decision === Decision.Approve ? 'Approve' : 'Reject',
            hiddenCondition: (user: CompanyUserSummary) =>
                user.publishState !== PublishState.Pending,
            action: (user: CompanyUserSummary) => this.execute(user, decision, companyId),
        };
    }

    private execute(user: CompanyUserSummary, decision: Decision, companyId?: number) {
        this.handleApproveOrRejectConfirmation(user, decision, companyId);
    }

    private async handleApproveOrRejectConfirmation(
        user: CompanyUserSummary,
        decision: Decision,
        companyId?: number
    ) {
        const buttons: SideDrawerButton<ApproveOrRejectConfirmationData>[] = [
            {
                label: decision === Decision.Approve ? 'Approve' : 'Reject',
                posture: SideDrawerButtonPosture.Primary,
                reason: DynamicComponentExitReason.Confirm,
                checkEnabled: data => {
                    if (!data || !data.comments) {
                        return false;
                    }
                    return data.comments.length > 0;
                },
            },
            {
                label: 'Cancel',
                posture: SideDrawerButtonPosture.Tertiary,
                reason: DynamicComponentExitReason.Cancel,
            },
        ];

        let comments = '';

        const result = await this.sideDrawerService.open<ApproveOrRejectConfirmationData>(
            setData =>
                html`<tm-bo-approve-or-reject-confirmation
                    .decision=${decision}
                    /* v8 ignore next 2 */
                    @comment-change=${(e: CustomEvent) => {
                        comments = e.detail;
                        setData({ comments });
                    }}
                ></tm-bo-approve-or-reject-confirmation>`,
            decision === Decision.Approve
                ? ApproveOrRejectConfirmation.approvalTitle
                : ApproveOrRejectConfirmation.rejectionTitle,

            // Hack: We are having to cast to an unknown type because the side-drawer service
            // is not properly handling our data type. This is a temporary fix until the service
            // is updated to handle the correct data type.
            { align: 'right', buttons: buttons as SideDrawerButton<unknown>[] }
        );

        const exitReasons = [
            DynamicComponentExitReason.Cancel,
            DynamicComponentExitReason.ForceClose,
        ];

        if (exitReasons.includes(result.reason)) {
            return;
        }

        try {
            if (!user.id) {
                throw new Error('User ID is required to discard company user changes');
            }

            let success = false;
            if (decision === Decision.Approve) {
                success = await this.companyUserService.approveCompanyUser(
                    user.id,
                    comments,
                    companyId
                );
            } else {
                success = await this.companyUserService.rejectCompanyUser(
                    user.id,
                    comments,
                    companyId
                );
            }
            if (!success) {
                throw new Error('Failed to discard company user changes');
            }

            this.toastService.show(
                `Company User ${decision === Decision.Approve ? 'Approved' : 'Rejected'} Successfully`,
                TmToastPosture.Success
            );
        } catch {
            this.toastService.show(
                `Failed to ${decision === Decision.Approve ? 'approve' : 'reject'} company user`,
                TmToastPosture.Danger,
                true
            );
        }
    }
}
